import {
  KnownDeliveryDtoDeliveryType,
  KnownHubDetailsDtoStatus,
  KnownHubDetailsDtoType,
  KnownPartyDtoType,
  KnownProjectModuleSettingsDtoName,
  KnownProjectModuleSettingsDtoStatus,
  PartyDto
} from '@groundfloor-platform/mgmt-service-g-sdk';
import { useMemo } from 'react';
import { useQueryGetProject } from '../../../../../core/queries/projects.queries';
import { useQueryGetHubDetails, useQueryListHubsDetails } from '../../../../lockers/hubs.queries';
import { useQueryListPartiesByReceivingProject } from '../../../deliveries.queries';
import { deliveryTypeToKioskTransactionTypeMap } from '../../../maps/delivery-type-to-kiosk-transaction-type.map';
import { ReceiveDeliverySelectOption } from './receive-delivery-form-config';
import { naturalSort } from '../../../../../core/util/natural-sort.util';
import { useQueryGetProjectModules } from '../../../../project-modules/queries';
import { deliveryTypeToProjectModuleMap } from '../../../maps/delivery-type-to-project-module.map';
import { deliveryTypeToInternalOrExternalMap } from '../../../maps/delivery-type-to-internal-or-external.map';
import { InboundParcel } from '../../../../../ui/components/pure/icons/InboundParcel.icon';
import { InboundDryCleaning } from '../../../../../ui/components/pure/icons/InboundDryCleaning.icon';
import { OutboundDryCleaning } from '../../../../../ui/components/pure/icons/OutboundDryCleaning.icon';
import { InboundMail } from '../../../../../ui/components/pure/icons/InboundMail.icon';
import { OutboundMail } from '../../../../../ui/components/pure/icons/OutboundMail.icon';
import { PeerToPeer } from '../../../../../ui/components/pure/icons/PeerToPeer.icon';

export interface ReceiveDeliveryFormDataProps {
  projectID: string;
  selectedProjectID: string;
  selectedDeliveryType: KnownDeliveryDtoDeliveryType;
  selectedHubID: string;
}

const deliveryTypePriorityOrder = [
  KnownDeliveryDtoDeliveryType.InboundParcel,
  KnownDeliveryDtoDeliveryType.InboundMail,
  KnownDeliveryDtoDeliveryType.OutboundMail,
  KnownDeliveryDtoDeliveryType.OutboundDryCleaning,
  KnownDeliveryDtoDeliveryType.InboundDryCleaning,
  KnownDeliveryDtoDeliveryType.PeerToPeer
];

export function useReceiveDeliveryFormData(props: ReceiveDeliveryFormDataProps) {
  // Project Options
  const project = useQueryGetProject(props.projectID);
  const projectModules = useQueryGetProjectModules(props.projectID);

  const projectOptions = useMemo((): ReceiveDeliverySelectOption[] => {
    if (!project.data) {
      return [];
    }
    return [
      {
        label: project.data.name,
        value: project.data.id,
        imageURL: project.data.imageURL
      }
    ];
  }, [project]);
  // Hub and DeliveryType Options
  const hubs = useQueryListHubsDetails(props.selectedProjectID);

  const filteredHubs = useMemo(() => {
    return (hubs.data || []).filter(
      (hub) => hub.status === KnownHubDetailsDtoStatus.Active && hub.type === KnownHubDetailsDtoType.KiosklessSpacesHub
    );
  }, [hubs.data]);

  const deliveryTypeOptions = useMemo((): ReceiveDeliverySelectOption[] => {
    const modules = projectModules.data || [];

    return modules
      .filter((module) => module.data.status === KnownProjectModuleSettingsDtoStatus.Active)
      .flatMap((module) => {
        switch (module.schema.name) {
          case KnownProjectModuleSettingsDtoName.InboundParcel:
            return {
              value: KnownDeliveryDtoDeliveryType.InboundParcel,
              label: 'Inbound Parcel',
              Icon: () => <InboundParcel style={{ width: '21px', height: '21px', marginRight: '8px' }} />
            };
          case KnownProjectModuleSettingsDtoName.InboundMail:
            return {
              value: KnownDeliveryDtoDeliveryType.InboundMail,
              label: 'Inbound Mail',
              Icon: () => <InboundMail style={{ width: '21px', height: '21px', marginRight: '8px' }} />
            };
          case KnownProjectModuleSettingsDtoName.OutboundMail:
            return {
              value: KnownDeliveryDtoDeliveryType.OutboundMail,
              label: 'Outbound Mail',
              Icon: () => <OutboundMail style={{ width: '21px', height: '21px', marginRight: '8px' }} />
            };
          case KnownProjectModuleSettingsDtoName.DryCleaning:
            return [
              {
                value: KnownDeliveryDtoDeliveryType.InboundDryCleaning,
                label: 'Inbound Dry Cleaning',
                Icon: () => <InboundDryCleaning style={{ width: '21px', height: '21px', marginRight: '8px' }} />
              },
              {
                value: KnownDeliveryDtoDeliveryType.OutboundDryCleaning,
                label: 'Outbound Dry Cleaning',
                Icon: () => <OutboundDryCleaning style={{ width: '21px', height: '21px', marginRight: '8px' }} />
              }
            ];
          case KnownProjectModuleSettingsDtoName.PeerToPeer:
            return {
              value: KnownDeliveryDtoDeliveryType.PeerToPeer,
              label: 'Peer to Peer',
              Icon: () => <PeerToPeer style={{ width: '21px', height: '21px', marginRight: '8px' }} />
            };
          case KnownProjectModuleSettingsDtoName.ClickAndCollect:
            return []; // Retail Order is not included in the options
          default:
            return [];
        }
      })
      .sort((a, b) => {
        // Sort by priority order defined in deliveryTypePriorityOrder
        const aIndex = deliveryTypePriorityOrder.indexOf(a.value as KnownDeliveryDtoDeliveryType);
        const bIndex = deliveryTypePriorityOrder.indexOf(b.value as KnownDeliveryDtoDeliveryType);
        return aIndex - bIndex;
      });
  }, [projectModules.data]);

  const hubOptions = useMemo((): ReceiveDeliverySelectOption[] => {
    const internalOrExternal = deliveryTypeToInternalOrExternalMap[props.selectedDeliveryType];
    const moduleName = deliveryTypeToProjectModuleMap[props.selectedDeliveryType];

    return filteredHubs
      .filter((hub) => {
        // Note: We are assuming that the Hub has Spaces (not lockers) because we are filtering by KiosklessSpacesHub type.
        const deliveryContainers = (hub.sections || []).flatMap((section) => section.spaces);

        return deliveryContainers.some((container) => {
          const selectedModules =
            internalOrExternal === 'internal'
              ? container.selectedInternalProjectModules
              : container.selectedExternalProjectModules;
          // If the container has been selected for EITHER internal or external modules, then we need to check if the current module is included
          if (
            container.selectedInternalProjectModules.length > 0 ||
            container.selectedExternalProjectModules.length > 0
          ) {
            return selectedModules?.includes(moduleName);
          }
          return true;
        });
      })
      .map((hub) => ({
        label: hub.name,
        value: hub.id
      }));
  }, [filteredHubs, props.selectedDeliveryType]);

  // Space Options
  const hubDetails = useQueryGetHubDetails(props.selectedHubID);

  const spaceOptions = useMemo((): ReceiveDeliverySelectOption[] => {
    debugger;
    const internalOrExternal = deliveryTypeToInternalOrExternalMap[props.selectedDeliveryType];
    const moduleName = deliveryTypeToProjectModuleMap[props.selectedDeliveryType];

    const options = (hubDetails.data?.sections || [])
      .map((section) =>
        section.spaces
          .filter((space) => {
            const selectedModules =
              internalOrExternal === 'internal'
                ? space.selectedInternalProjectModules
                : space.selectedExternalProjectModules;
            // If the space has been selected for EITHER internal or external modules, then we need to check if the current module is included
            if (space.selectedInternalProjectModules.length > 0 || space.selectedExternalProjectModules.length > 0) {
              return selectedModules?.includes(moduleName);
            }
            // If the space is not selected for ANY modules, it is available for all modules
            return true;
          })
          .map((space) => ({ label: `${section.name} - ${space.name}`, value: space.id }))
      )
      .flat();
    return naturalSort(options).asc((option) => option.label);
  }, [hubDetails.data?.sections, props.selectedDeliveryType]);

  // Delivery and Receiving Party Options
  const deliverTransactionType = deliveryTypeToKioskTransactionTypeMap[props.selectedDeliveryType]?.deliver;
  const collectTransactionType = deliveryTypeToKioskTransactionTypeMap[props.selectedDeliveryType]?.collect;

  const deliveringParties = useQueryListPartiesByReceivingProject(props.selectedProjectID, deliverTransactionType);
  const receivingParties = useQueryListPartiesByReceivingProject(props.selectedProjectID, collectTransactionType);

  const deliveringPartyType = useMemo(() => {
    return getPartyTypeFromList(deliveringParties.data || []);
  }, [deliveringParties.data]);

  const receivingPartyType = useMemo(() => {
    return getPartyTypeFromList(receivingParties.data || []);
  }, [receivingParties.data]);

  const deliveringPartyOptions = useMemo((): ReceiveDeliverySelectOption[] => {
    if (deliveringPartyType === 'occupant') {
      return (deliveringParties.data || []).sort(sortOccupantOptions).map(occupantToOccupantOption);
    } else {
      return (deliveringParties.data || []).sort(sortCarrierOptions).map(carrierToCarrierOption);
    }
  }, [deliveringParties.data, deliveringPartyType]);

  const receivingPartyOptions = useMemo((): ReceiveDeliverySelectOption[] => {
    if (receivingPartyType === 'occupant') {
      return (receivingParties.data || []).sort(sortOccupantOptions).map(occupantToOccupantOption);
    } else {
      return (receivingParties.data || []).sort(sortCarrierOptions).map(carrierToCarrierOption);
    }
  }, [receivingParties.data, receivingPartyType]);

  // Set Default Values
  const defaultProject = useMemo(() => {
    return projectOptions.find((option) => option.value === props.projectID);
  }, [projectOptions, props.projectID]);

  const defaultDeliveryType = useMemo(() => {
    return deliveryTypeOptions[0];
  }, [deliveryTypeOptions]);

  const defaultDeliveringParty = useMemo(() => {
    return deliveringPartyOptions.find((option) => option.value === props.projectID);
  }, [deliveringPartyOptions, props.projectID]);

  const defaultHub = useMemo(() => {
    if (hubOptions.length === 1) {
      return hubOptions[0];
    }
    return undefined;
  }, [hubOptions]);

  const defaultDeliveryContainer = useMemo(() => {
    if (spaceOptions.length === 1) {
      return spaceOptions[0];
    }
    return undefined;
  }, [spaceOptions]);

  // Set loading state
  const isLoading =
    project.isLoading ||
    hubs.isLoading ||
    hubDetails.isLoading ||
    deliveringParties.isLoading ||
    receivingParties.isLoading;

  return {
    state: {
      isLoading
    },
    options: {
      project: projectOptions,
      deliveryType: deliveryTypeOptions,
      hub: hubOptions,
      space: spaceOptions,
      deliveringParty: deliveringPartyOptions,
      receivingParty: receivingPartyOptions
    },
    defaults: {
      project: defaultProject,
      deliveryType: defaultDeliveryType,
      deliveringParty: defaultDeliveringParty,
      hub: defaultHub,
      deliveryContainer: defaultDeliveryContainer
    },
    info: {
      deliveringPartyType,
      receivingPartyType
    }
  } as const;
}

function sortCarrierOptions(a: PartyDto, b: PartyDto) {
  // Sort by party type first (descending order so Projects come first)
  const aType = a.type;
  const bType = b.type;
  if (aType > bType) {
    return -1;
  }
  if (aType > bType) {
    return 1;
  }
  // Sort by name second
  return a.name.localeCompare(b.name);
}

function sortOccupantOptions(a: PartyDto, b: PartyDto) {
  return a.name.localeCompare(b.name);
}

function carrierToCarrierOption(carrier: PartyDto): ReceiveDeliverySelectOption {
  return {
    label: carrier.name,
    value: carrier.id,
    imageURL: carrier.imageURL,
    category: carrier.type
  };
}

function occupantToOccupantOption(occupant: PartyDto): ReceiveDeliverySelectOption {
  return {
    label: occupant.name,
    value: occupant.id,
    unit: occupant.unit
  };
}

function getPartyTypeFromList(partyList: PartyDto[]) {
  if (partyList.length === 0) {
    return undefined;
  }
  const partyTypes = partyList.map((party) => party.type);
  const partyOptionsType = partyTypes.includes(KnownPartyDtoType.Occupant) ? 'occupant' : 'provider';
  return partyOptionsType;
}
